import { create } from 'zustand';
import type { AuthUser, MemberRole } from '@repo/shared-types';

interface AuthOrg {
  id: string;
  name: string;
  slug: string;
  logoUrl?: string | null;
}

interface AuthState {
  user: AuthUser | null;
  organization: AuthOrg | null;
  role: MemberRole | null;
  accessToken: string | null;
  refreshToken: string | null;
  impersonatedBy: string | null;
  isLoading: boolean;

  setAuth: (data: {
    user: AuthUser;
    organization: AuthOrg;
    tokens: { accessToken: string; refreshToken?: string | null };
    role?: MemberRole;
    impersonatedBy?: string | null;
  }) => void;
  setUser: (user: AuthUser) => void;
  setOrganization: (organization: AuthOrg) => void;
  setRole: (role: MemberRole) => void;
  updateTokens: (tokens: { accessToken: string; refreshToken: string }) => void;
  clearAuth: () => void;
  setLoading: (loading: boolean) => void;
  hydrateFromStorage: () => boolean;
}

const TOKEN_KEY = 'fos_access_token';
const REFRESH_KEY = 'fos_refresh_token';
const USER_KEY = 'fos_user';
const ORG_KEY = 'fos_org';
const ROLE_KEY = 'fos_role';
const IMPERSONATED_BY_KEY = 'fos_impersonated_by';

/**
 * Is there a session in storage at all? Used by /login and /register to bounce
 * an already-signed-in visitor before the store has hydrated.
 *
 * Exists so those two pages stop calling localStorage.getItem('fos_access_token')
 * themselves: they were the only two places in the dashboard that read an auth
 * key outside this file, and once storage becomes conditional (impersonation
 * lives in sessionStorage) a hard-coded localStorage read is simply wrong.
 */
export function hasStoredSession(): boolean {
  return Boolean(sessionStorage.getItem(TOKEN_KEY) ?? localStorage.getItem(TOKEN_KEY));
}

/**
 * An impersonated session lives in sessionStorage, a real one in localStorage.
 *
 * sessionStorage is per-tab, so an admin who is signed in as themselves in
 * another tab keeps that session while impersonating in this one — and exiting
 * here cannot sign them out there.
 */
function storageFor(impersonated: boolean): Storage {
  return impersonated ? sessionStorage : localStorage;
}

function persistToStorage(state: Partial<AuthState>, impersonated: boolean) {
  const s = storageFor(impersonated);
  if (state.accessToken) s.setItem(TOKEN_KEY, state.accessToken);
  if (state.refreshToken) s.setItem(REFRESH_KEY, state.refreshToken);
  if (state.user) s.setItem(USER_KEY, JSON.stringify(state.user));
  if (state.organization) s.setItem(ORG_KEY, JSON.stringify(state.organization));
  if (state.role) s.setItem(ROLE_KEY, state.role);
  if (state.impersonatedBy) s.setItem(IMPERSONATED_BY_KEY, state.impersonatedBy);
}

function clearStorage(impersonated: boolean) {
  const s = storageFor(impersonated);
  [TOKEN_KEY, REFRESH_KEY, USER_KEY, ORG_KEY, ROLE_KEY, IMPERSONATED_BY_KEY].forEach((k) =>
    s.removeItem(k),
  );
}

/**
 * The signed-in org id straight out of persisted storage, WITHOUT waiting for
 * `hydrateFromStorage()` to run — the auth store's `organization` is still null
 * while modules are being evaluated.
 *
 * branchStore needs it at module-init time so a remembered branch is already
 * applied on the very first render (THI-137 final review F4); anything inside
 * React should read `organization?.id` from the store instead.
 */
export function readPersistedOrgId(): string | null {
  try {
    const impersonatedBy = sessionStorage.getItem(IMPERSONATED_BY_KEY);
    const raw = storageFor(impersonatedBy !== null).getItem(ORG_KEY);
    if (!raw) return null;
    return (JSON.parse(raw) as AuthOrg).id ?? null;
  } catch {
    return null; // private mode, blocked site data, or a corrupt row
  }
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  organization: null,
  role: null,
  accessToken: null,
  refreshToken: null,
  impersonatedBy: null,
  isLoading: true,

  setAuth: ({ user, organization, tokens, role, impersonatedBy }) => {
    // useAuthMe re-calls setAuth after every /auth/me and does not know about
    // impersonation, so an omitted flag must mean "keep what we have", never
    // "this is a real session".
    const impersonated = impersonatedBy !== undefined ? impersonatedBy : get().impersonatedBy;
    const state = {
      user,
      organization,
      role: role ?? null,
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken ?? null,
      impersonatedBy: impersonated,
      isLoading: false,
    };
    persistToStorage(state, impersonated !== null);
    // Whole-branch review: a resolved REAL session (impersonatedBy === null)
    // must not leave a stale impersonation behind in sessionStorage.
    // hydrateFromStorage keys entirely off sessionStorage's IMPERSONATED_BY_KEY,
    // so a leftover entry there would resurrect a dead impersonation instead
    // of the real session on the next reload — even though the real session
    // was just correctly written to localStorage above. No call site does
    // this today, but nothing stops a future one, so this stays defensive.
    if (impersonated === null) clearStorage(true);
    set(state);
  },

  setUser: (user) => {
    storageFor(get().impersonatedBy !== null).setItem(USER_KEY, JSON.stringify(user));
    set({ user });
  },

  setOrganization: (organization) => {
    storageFor(get().impersonatedBy !== null).setItem(ORG_KEY, JSON.stringify(organization));
    set({ organization });
  },

  setRole: (role) => {
    storageFor(get().impersonatedBy !== null).setItem(ROLE_KEY, role);
    set({ role });
  },

  updateTokens: (tokens) => {
    const s = storageFor(get().impersonatedBy !== null);
    s.setItem(TOKEN_KEY, tokens.accessToken);
    s.setItem(REFRESH_KEY, tokens.refreshToken);
    set({ accessToken: tokens.accessToken, refreshToken: tokens.refreshToken });
  },

  clearAuth: () => {
    // Only the storage this session came from. Clearing both would end the
    // impersonation and sign the admin out of their own account at once.
    clearStorage(get().impersonatedBy !== null);
    set({
      user: null,
      organization: null,
      role: null,
      accessToken: null,
      refreshToken: null,
      impersonatedBy: null,
      isLoading: false,
    });
  },

  setLoading: (isLoading) => set({ isLoading }),

  hydrateFromStorage: () => {
    // sessionStorage wins: if this tab holds an impersonation, that is the
    // session for this tab even when a real one sits in localStorage.
    const impersonatedBy = sessionStorage.getItem(IMPERSONATED_BY_KEY);
    const s = storageFor(impersonatedBy !== null);

    const accessToken = s.getItem(TOKEN_KEY);
    const refreshToken = s.getItem(REFRESH_KEY);
    const userRaw = s.getItem(USER_KEY);
    const orgRaw = s.getItem(ORG_KEY);
    const role = s.getItem(ROLE_KEY) as MemberRole | null;

    // An impersonated session has no refresh token by design, so requiring one
    // here would bounce the tab to /login on every reload.
    const needsRefresh = impersonatedBy === null;
    if (!accessToken || (needsRefresh && !refreshToken) || !userRaw || !orgRaw) {
      set({ isLoading: false });
      return false;
    }

    try {
      const user = JSON.parse(userRaw) as AuthUser;
      const organization = JSON.parse(orgRaw) as AuthOrg;
      set({ user, organization, role, accessToken, refreshToken, impersonatedBy, isLoading: false });
      return true;
    } catch {
      clearStorage(impersonatedBy !== null);
      set({ isLoading: false });
      return false;
    }
  },
}));
